/**
 * Risk Component Chart
 * ====================
 * Horizontal bar chart showing the 4 risk components:
 * - Geographic (25%)
 * - Product (15%)
 * - Channel (20%)
 * - Customer (40%)
 *
 * Each bar shows the component score with color-coded severity.
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';

// Component colors and labels
const COMPONENT_CONFIG = {
  customer: {
    label: 'Customer',
    color: '#10b981', // green
    weight: 40,
  },
  geographic: {
    label: 'Geographic',
    color: '#3b82f6', // blue
    weight: 25,
  },
  channel: {
    label: 'Channel',
    color: '#f59e0b', // amber
    weight: 20,
  },
  product: {
    label: 'Product',
    color: '#8b5cf6', // purple
    weight: 15,
  },
};

// Order components by weight (largest first)
const COMPONENT_ORDER = ['customer', 'geographic', 'channel', 'product'];

/**
 * Get color based on score severity
 */
function getScoreColor(score) {
  if (score >= 70) return '#ef4444'; // red - high risk
  if (score >= 40) return '#f59e0b'; // amber - medium risk
  return '#10b981'; // green - low risk
}

/**
 * Get data quality indicator
 */
function DataQualityBadge({ quality }) {
  if (quality === 'complete') return null;

  const config = {
    partial: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', label: 'Incomplete', title: 'Some data missing for this component' },
    missing: { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', label: 'No Data', title: 'No data available for this component' },
  };

  const c = config[quality] || config.partial;

  return (
    <span
      title={c.title}
      style={{
        fontSize: '9px',
        padding: '2px 5px',
        borderRadius: '3px',
        background: c.bg,
        color: c.color,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        cursor: 'help',
      }}>
      {c.label}
    </span>
  );
}

/**
 * Single component bar
 */
function ComponentBar({ component, isLast }) {
  const config = COMPONENT_CONFIG[component.component] || {};
  const scoreColor = getScoreColor(component.score);
  const barWidth = Math.min(100, Math.max(0, component.score));
  const isPartial = component.data_quality === 'partial' || component.data_quality === 'missing';

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '90px 1fr auto',
      alignItems: 'center',
      gap: '16px',
      padding: '10px 0',
      borderBottom: isLast ? 'none' : '1px solid var(--border-color)',
    }}>
      {/* Label */}
      <div style={{
        fontSize: '13px',
        fontWeight: 500,
        color: 'var(--text-primary)',
      }}>
        {config.label}
      </div>

      {/* Bar */}
      <div style={{
        height: '8px',
        background: 'var(--bg-tertiary)',
        borderRadius: '4px',
        overflow: 'hidden',
        minWidth: '100px',
      }}>
        <div style={{
          height: '100%',
          width: `${barWidth}%`,
          background: scoreColor,
          borderRadius: '4px',
          transition: 'width 0.3s ease',
        }} />
      </div>

      {/* Score column - fixed width, proper alignment */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        minWidth: '100px',
        justifyContent: 'flex-end',
      }}>
        {isPartial && <DataQualityBadge quality={component.data_quality} />}
        <div style={{
          textAlign: 'right',
          whiteSpace: 'nowrap',
        }}>
          <span style={{
            fontSize: '14px',
            fontWeight: 600,
            color: scoreColor,
          }}>
            {component.weighted_score}
          </span>
          <span style={{
            fontSize: '12px',
            color: scoreColor,
            fontWeight: 500,
            marginLeft: '2px',
          }}>
            pts
          </span>
          <span style={{
            fontSize: '11px',
            color: 'var(--text-muted)',
            fontWeight: 400,
            marginLeft: '4px',
          }}>
            ({config.weight}%)
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Main chart component
 */
export function RiskComponentChart({ components = [], dataCompleteness = 100, missingData = [] }) {
  // Sort components by our defined order
  const sortedComponents = [...components].sort((a, b) => {
    const aIdx = COMPONENT_ORDER.indexOf(a.component);
    const bIdx = COMPONENT_ORDER.indexOf(b.component);
    return aIdx - bIdx;
  });

  // Calculate total
  const total = components.reduce((sum, c) => sum + (c.weighted_score || 0), 0);

  return (
    <div style={{
      background: 'var(--bg-secondary)',
      borderRadius: '8px',
      padding: '16px',
      border: '1px solid var(--border-color)',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
      }}>
        <span style={{
          fontSize: '11px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-muted)',
        }}>
          Component Breakdown
        </span>

        <span style={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--text-secondary)',
        }}>
          Total: {total} pts
        </span>
      </div>

      {/* Component bars */}
      <div>
        {sortedComponents.map((component, idx) => (
          <ComponentBar
            key={component.component}
            component={component}
            isLast={idx === sortedComponents.length - 1}
          />
        ))}
      </div>

      {/* Missing data warning */}
      {missingData.length > 0 && (
        <div style={{
          marginTop: '12px',
          padding: '8px 12px',
          background: 'rgba(245, 158, 11, 0.1)',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '8px',
        }}>
          <AlertTriangle size={14} style={{ color: '#f59e0b', marginTop: '2px', flexShrink: 0 }} />
          <div style={{ fontSize: '12px', color: '#f59e0b' }}>
            <strong>Incomplete data ({dataCompleteness}%):</strong>{' '}
            {missingData.join(', ')}
          </div>
        </div>
      )}
    </div>
  );
}

export default RiskComponentChart;
